import { useEffect, useMemo, useRef, useState } from "react";
import { Download, RotateCcw, Upload, Undo2, Redo2, Code2, ChevronLeft, LineChart, Cpu, Layers, CircleDot, Play, Wand2, Network, Rocket, Gift } from "lucide-react";
import { AppTopChrome } from "@/components/AppTopChrome";
import { SimChartPanel } from "@/components/algo/SimChartPanel";
import { Playground } from "@/components/algo/Playground";
import { PlaygroundWizard } from "@/components/algo/PlaygroundWizard";
import { WalletsButton } from "@/components/algo/WalletsButton";
import { LaunchPanel } from "@/components/algo/LaunchPanel";
import { LiveTape } from "@/components/algo/LiveTape";
import { AlgoAdvancedIde } from "@/components/algo/AlgoAdvancedIde";
import { BubbleMapPanel } from "@/components/algo/BubbleMapPanel";
import { NurseryPanel } from "@/components/NurseryPanel";
import { uid, type WalletLedger } from "@/lib/algo/choreography";
import { makeSampleGraph, normalizeLoadedGraph, runGraph, type FlowGraph } from "@/lib/algo/graph";
import { CaChartPanel } from "@/components/CaChartPanel";
import { AirdropsTab } from "./AirdropsTab";
import { useStreamingEngine } from "@/lib/algo/useStreamingEngine";
import { useBackend } from "@/lib/algo/useBackend";
import { reconstructFlowGraph } from "@/lib/algo/heliusReconstruct";
import { ForeignTokenTape } from "@/components/algo/ForeignTokenTape";

import { validateGraph } from "@/lib/algo/validate";
import { walletFundNeeds } from "@/lib/algo/fundNeeds";
import { useApp } from "@/context/AppCtx";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import "@/algo-launcher.css";

export function AlgoDeployWorkspace() {
  const { layoutMode, presavedGraphs, savePresavedGraph, caMintInput, addAppToast } = useApp() as any;
  const isMobile = useIsMobile();
  const [graph, setGraph] = useState<FlowGraph>(() => makeSampleGraph());
  const [rightTab, setRightTab] = useState<"playground" | "wizard" | "launch" | "ide" | "bubblemap" | "airdrops">("wizard");
  const fileRef = useRef<HTMLInputElement | null>(null);

  // undo/redo history (user edits via `update` snapshot; background effects use setGraph directly)
  const histPast = useRef<FlowGraph[]>([]);
  const histFuture = useRef<FlowGraph[]>([]);
  const update = (fn: (draft: FlowGraph) => void) =>
    setGraph((prev) => {
      const next = structuredClone(prev);
      fn(next);
      if (histPast.current[histPast.current.length - 1] !== prev) {
        histPast.current.push(prev);
        if (histPast.current.length > 80) histPast.current.shift();
        histFuture.current = [];
      }
      return next;
    });
  const undo = () => { const prev = histPast.current.pop(); if (prev === undefined) return; histFuture.current.push(graph); setGraph(prev); };
  const redo = () => { const nxt = histFuture.current.pop(); if (nxt === undefined) return; histPast.current.push(graph); setGraph(nxt); };

  const backend = useBackend(update);

  // Use alpha mode directly since solclaw's backend supports it
  const live = backend.online === true;

  const createWalletAt = (x: number, y: number) => {
    if (live) { void backend.createReal(1, { x, y }); return; }
    update((d) => { const i = d.wallets.length; d.wallets.push({ id: uid("w"), label: `Wallet ${i + 1}`, group: "core", fundingSol: 1, enabled: true, x, y }); });
  };

  const handleSwitchContext = (mint: string) => {
    const existing = presavedGraphs?.find((g: any) => g.name === mint);
    if (existing) {
      setGraph(existing);
      setRightTab("playground");
    } else {
      update((d) => {
        d.name = mint; 
      });
    }
  };

  useEffect(() => {
    if (caMintInput && caMintInput !== graph.name) {
      // Only switch context if it looks like a valid mint
      if (caMintInput.length >= 32 && !caMintInput.includes(" ")) {
        handleSwitchContext(caMintInput);
        setRightTab("playground"); // Ensure playground is open for foreign tokens
      }
    }
  }, [caMintInput]);

  const isForeignToken = useMemo(() => {
    if (!graph.name) return false;
    return graph.name.length >= 32 && !graph.name.includes(" ");
  }, [graph.name]);

  const isExpandedNursery = !isForeignToken && (!graph.name || graph.name === "launch") && rightTab !== "playground";

  const initialMount = useRef(true);
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    if (graph && savePresavedGraph && !isForeignToken && graph.name !== "launch") {
      savePresavedGraph(graph);
    }
  }, [graph, isForeignToken, savePresavedGraph]);

  useEffect(() => {
    let alive = true;
    const apply = () => import("@/lib/algo/solPrice").then(({ fetchSolUsd }) => fetchSolUsd().then((v) => {
      if (alive && v) setGraph((d) => (d.solUsd === v ? d : { ...d, solUsd: v }));
    }));
    apply();
    const t = setInterval(apply, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const alphaSyncedRef = useRef(false);
  useEffect(() => {
    if (backend.online && !alphaSyncedRef.current && graph.wallets.some((w) => !w.publicKey)) {
      alphaSyncedRef.current = true;
      void backend.syncFromBackend();
    }
  }, [backend, graph.wallets]);

  useEffect(() => {
    setGraph((prev) => {
      const needs = walletFundNeeds(prev);
      let changed = false;
      const wallets = prev.wallets.map((w) => {
        const need = Math.round((needs.get(w.id) ?? 0) * 1000) / 1000;
        if (Math.abs((w.fundingSol ?? 0) - need) > 1e-9) { changed = true; return { ...w, fundingSol: need }; }
        return w;
      });
      return changed ? { ...prev, wallets } : prev;
    });
  }, [graph.edges, graph.devBuySol, graph.launch.devWalletId, graph.wallets.length]);

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamSpeed, setStreamSpeed] = useState(1);
  const streamingSim = useStreamingEngine(graph, isStreaming, streamSpeed);
  
  const staticSim = useMemo(() => runGraph(graph), [graph]);
  const sim = (isStreaming && streamingSim) ? streamingSim : staticSim;
  
  const validation = useMemo(() => validateGraph(graph), [graph]);
  const blocks = validation.issues.filter((i) => i.severity === "block").length;

  const funding = useMemo(() => {
    const needs = walletFundNeeds(graph);
    const needed = [...needs.values()].reduce((s, v) => s + v, 0);
    const funded = live ? [...needs.entries()].reduce((s, [id, need]) => s + Math.min(need, backend.balances.get(id)?.sol ?? 0), 0) : 0;
    return { funded, needed, live };
  }, [graph, live, backend.balances]);

  const ledgers = useMemo(() => {
    const m = new Map<string, WalletLedger>();
    for (const l of sim.ledgers) m.set(l.walletId, l);
    return m;
  }, [sim]);

  async function save() {
    const blob = new Blob([JSON.stringify(graph, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${graph.name.replace(/\s+/g, "-").toLowerCase() || "score"}.json`;
    a.click();
    URL.revokeObjectURL(a.href);

    // Also persist to backend
    try {
      await fetch("/api/flowgraph", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: graph.name || uid("graph"), data: graph })
      });
    } catch (e) {
      console.warn("Failed to persist graph to backend", e);
    }
  }
  function load(file: File) {
    file.text().then((t) => {
      try {
        setGraph(normalizeLoadedGraph(JSON.parse(t), graph.solUsd));
      } catch { addAppToast("That file isn't a valid Lily score.", "error"); }
    }).catch(() => addAppToast("Couldn't read that file.", "error"));
  }

  const handleReconstruct = async () => {
    const mint = prompt("Enter token mint address to reconstruct from Helius:");
    if (!mint) return;
    try {
      const newGraph = await reconstructFlowGraph(mint);
      setGraph(newGraph);
    } catch (err: any) {
      addAppToast("Failed to reconstruct: " + err.message, "error");
    }
  };

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <MobileAlgoWorkspace
        graph={graph}
        sim={sim}
        validation={validation}
        blocks={blocks}
        funding={funding}
        ledgers={ledgers}
        update={update}
        backend={backend}
        live={live}
        undo={undo}
        redo={redo}
        createWalletAt={createWalletAt}
        save={save}
        load={load}
        setGraph={setGraph}
      />
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-h-[100dvh] min-h-0 flex-col overflow-hidden bg-[var(--color-bg-editor)]">
      <AppTopChrome />
      <div className="algo-launcher-theme flex-1 flex flex-col min-h-0 relative z-10 app2">
        <div className="flex items-center justify-between bg-[var(--panel)] border-b border-[var(--line)] px-4 py-2 shrink-0">
          <div className="flex items-center gap-4">
            {!live ? (
              <span className="sim-badge" title="Demo — everything is off-chain. No real funds, wallets, or private keys.">DEMO</span>
            ) : (
              <span className="sim-badge alpha" title="Alpha — REAL on-chain execution with real SOL.">ALPHA</span>
            )}
            <div className="analysis flex items-center gap-3 bg-transparent border-none p-0 m-0">
              <Stat v={sim.finalState.complete ? "🎓 100%" : `${sim.bondingProgress.toFixed(1)}%`} l={sim.finalState.complete ? "Graduated" : "Bonding"} />
              <Stat v={`${graph.edges.length}`} l="Steps" />
              <Stat v={blocks > 0 ? `⛔ ${blocks}` : graph.edges.length ? "✓" : "—"} l={blocks > 0 ? "Blocked" : "Valid"} warn={blocks > 0} />
            </div>
          </div>

          <div className="topbar-actions flex items-center gap-2">
            <input className="field h-7 px-2 text-xs w-32" value={graph.name}
              onChange={(e) => update((d) => { d.name = e.target.value; })} />
            <WalletsButton graph={graph} ledgers={ledgers} update={update} backend={backend} live={live} />
            <div className="flex items-center gap-1 border-l border-[var(--line)] pl-2 ml-1">
              <button className="btn btn-sm btn-ghost p-1.5" title="Undo (⌘Z)" onClick={undo}><Undo2 size={14} /></button>
              <button className="btn btn-sm btn-ghost p-1.5" title="Redo (⌘⇧Z)" onClick={redo}><Redo2 size={14} /></button>
            </div>
            <div className="flex items-center gap-1 border-l border-[var(--line)] pl-2 ml-1">
              <button 
                className={`btn btn-sm px-3 flex items-center gap-1 whitespace-nowrap ${isStreaming ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"}`} 
                onClick={() => setIsStreaming(!isStreaming)}
              >
                {isStreaming ? <><span className="size-2 rounded-full shrink-0 bg-red-500 animate-pulse" /> Stop Stream</> : <><Play size={12} className="shrink-0" fill="currentColor" /> Live Stream</>}
              </button>
              {isStreaming && (
                <select 
                  className="bg-black/50 border border-white/10 rounded px-2 py-1 text-xs text-white" 
                  value={streamSpeed} 
                  onChange={e => setStreamSpeed(Number(e.target.value))}
                >
                  <option value={1}>1x Speed</option>
                  <option value={10}>10x Speed</option>
                  <option value={100}>100x Speed</option>
                  <option value={1000}>1000x Speed</option>
                </select>
              )}
            </div>
            <div className="flex items-center gap-1 border-l border-[var(--line)] pl-2 ml-1">
              <button className="btn btn-sm p-1.5 px-3" onClick={save}><Download size={14} /> Save</button>
              <button className="btn btn-sm p-1.5 px-3" onClick={() => fileRef.current?.click()}><Upload size={14} /> Load</button>
              <button className="btn btn-sm p-1.5 px-3 text-emerald-400 hover:bg-emerald-500/20 bg-emerald-500/10 border-emerald-500/20 border" onClick={handleReconstruct}><Layers size={14} /> Reconstruct</button>
              <button className="btn btn-sm btn-ghost p-1.5 ml-1" title="Reset to sample" onClick={() => setGraph(makeSampleGraph())}><RotateCcw size={14} /></button>
            </div>
            <input ref={fileRef} type="file" accept="application/json" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) load(f); e.target.value = ""; }} />
          </div>
        </div>

        <div className="flex-1 relative mt-2 px-3 pb-3 flex flex-col min-h-0 w-full">
          <PanelGroup direction="horizontal" className="w-full h-full flex-1">
            {!isExpandedNursery && (
              <>
                <Panel defaultSize={20} minSize={15} order={layoutMode === "lily" ? 3 : 0}>
                  <div className="h-full border-r border-[var(--line)] pr-2">
                    <NurseryPanel defaultTab="my" onOpenToken={handleSwitchContext} />
                  </div>
                </Panel>
                
                <PanelResizeHandle className="w-1.5 bg-[var(--line)] hover:bg-emerald-500/50 transition-colors cursor-col-resize rounded-full mx-1" />
              </>
            )}

            <Panel defaultSize={isExpandedNursery ? 60 : 40} minSize={20} order={layoutMode === "lily" ? 2 : 1}>
              <div className={layoutMode === "lily" ? "h-full w-full pl-2 flex flex-col" : "h-full w-full pr-2 flex flex-col"}>
                {isExpandedNursery ? (
                  <div className="panel chart-panel flex-1 flex flex-col min-h-0 bg-transparent border-0">
                    <div className="w-full h-full flex gap-3 overflow-x-auto custom-scrollbar pb-2">
                      <div className="flex-1 min-w-[280px] flex flex-col bg-black/40 border border-white/5 rounded-xl overflow-hidden shadow-lg shadow-black/50">
                        <div className="py-2.5 px-4 bg-emerald-500/10 border-b border-emerald-500/20 text-center font-black uppercase tracking-widest text-[11px] text-emerald-400">Your Tokens</div>
                        <NurseryPanel defaultTab="my" forceTab="my" onOpenToken={handleSwitchContext} />
                      </div>
                      <div className="flex-1 min-w-[280px] flex flex-col bg-black/40 border border-white/5 rounded-xl overflow-hidden shadow-lg shadow-black/50">
                        <div className="py-2.5 px-4 bg-white/5 border-b border-white/10 text-center font-black uppercase tracking-widest text-[11px] text-white/70">New Pairs</div>
                        <NurseryPanel defaultTab="new" forceTab="new" onOpenToken={handleSwitchContext} />
                      </div>
                      <div className="flex-1 min-w-[280px] flex flex-col bg-black/40 border border-white/5 rounded-xl overflow-hidden shadow-lg shadow-black/50">
                        <div className="py-2.5 px-4 bg-amber-500/10 border-b border-amber-500/20 text-center font-black uppercase tracking-widest text-[11px] text-amber-400">Graduating</div>
                        <NurseryPanel defaultTab="grad" forceTab="grad" onOpenToken={handleSwitchContext} />
                      </div>
                      <div className="flex-1 min-w-[280px] flex flex-col bg-black/40 border border-white/5 rounded-xl overflow-hidden shadow-lg shadow-black/50">
                        <div className="py-2.5 px-4 bg-cyan-500/10 border-b border-cyan-500/20 text-center font-black uppercase tracking-widest text-[11px] text-cyan-400">Bonded</div>
                        <NurseryPanel defaultTab="bonded" forceTab="bonded" onOpenToken={handleSwitchContext} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="panel chart-panel flex-1 flex flex-col min-h-0">
                    <div className="panel-head shrink-0">
                      {isForeignToken ? (
                        <h2 title="Live view of a deployed token from the market.">Live Chart <span className="small muted" style={{ fontWeight: 500 }}>· foreign token</span></h2>
                      ) : (
                        <>
                          <h2 title="A modeled preview of the pre-bond bonding curve — approximate, not a guarantee of on-chain fills.">Simulated chart <span className="small muted" style={{ fontWeight: 500 }}>· approximate</span></h2>
                          <div className="row small">
                            <label className="lbl" style={{ margin: 0 }}>dev buy (SOL)</label>
                            <input className="field" style={{ width: 60 }} type="number" min="0" step="0.1" value={graph.devBuySol}
                              onChange={(e) => update((d) => { d.devBuySol = Math.max(0, Number(e.target.value) || 0); })} />
                            <span className="small muted" title="Live SOL price">SOL ${graph.solUsd.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <PanelGroup direction="horizontal" className="flex-1 min-h-0 w-full h-full">
                      {isForeignToken ? (
                        <>
                          <Panel defaultSize={75} minSize={20}>
                            <div className="w-full h-full relative overflow-hidden flex flex-col p-1 mr-1">
                              <CaChartPanel mint={graph.name} isFocus={true} />
                            </div>
                          </Panel>
                          <PanelResizeHandle className="w-1.5 bg-[var(--line)] hover:bg-emerald-500/50 transition-colors cursor-col-resize rounded-full mx-0.5" />
                          <Panel defaultSize={25} minSize={10}>
                            <div className="h-full ml-1 relative w-full overflow-hidden flex flex-col">
                              <ForeignTokenTape mint={graph.name} />
                            </div>
                          </Panel>
                        </>
                      ) : (
                        <>
                          <Panel defaultSize={75} minSize={20}>
                            <div className="chart-wrap flex-1 min-h-0 flex flex-col h-full mr-1 w-full">
                              <SimChartPanel prints={sim.prints} markers={sim.markers} solUsd={graph.solUsd} title="" />
                            </div>
                          </Panel>
                          <PanelResizeHandle className="w-1.5 bg-[var(--line)] hover:bg-emerald-500/50 transition-colors cursor-col-resize rounded-full mx-0.5" />
                          <Panel defaultSize={25} minSize={10}>
                            <div className="h-full ml-1 relative w-full">
                              <LiveTape prints={sim.prints} />
                            </div>
                          </Panel>
                        </>
                      )}
                    </PanelGroup>
                  </div>
                )}
              </div>
            </Panel>

            <PanelResizeHandle className="w-1.5 bg-[var(--line)] hover:bg-emerald-500/50 transition-colors cursor-col-resize rounded-full mx-1" />

            <Panel defaultSize={40} minSize={20} order={layoutMode === "lily" ? 1 : 2}>
              <div className={layoutMode === "lily" ? "h-full w-full pr-2 flex flex-col min-h-0" : "h-full w-full pl-2 flex flex-col min-h-0"}>
                <div className="rightcol flex-1 flex flex-col min-h-0 w-full">
                  <div className="pg-tabs shrink-0 overflow-x-auto whitespace-nowrap custom-scrollbar pb-1">
                    <button className={`flex items-center shrink-0 ${rightTab === "wizard" ? "on" : ""}`} onClick={() => setRightTab("wizard")}><Wand2 size={13} className="mr-1.5" /> Wizard</button>
                    <button className={`flex items-center shrink-0 ${rightTab === "playground" ? "on" : ""}`} onClick={() => setRightTab("playground")}><Network size={13} className="mr-1.5" /> Graph</button>
                    <button className={`flex items-center shrink-0 ${rightTab === "launch" ? "on" : ""}`} onClick={() => setRightTab("launch")}><Rocket size={13} className="mr-1.5" /> Launch</button>
                    <button className={`flex items-center shrink-0 ${rightTab === "airdrops" ? "on" : ""}`} onClick={() => setRightTab("airdrops")}><Gift size={13} className="mr-1.5" /> Airdrops</button>
                    <button className={`flex items-center shrink-0 ${rightTab === "bubblemap" ? "on" : ""}`} onClick={() => setRightTab("bubblemap")}><CircleDot size={13} className="mr-1.5" /> Bubbles</button>
                    <button className={`flex items-center shrink-0 ${rightTab === "ide" ? "on" : ""}`} onClick={() => setRightTab("ide")}><Code2 size={13} className="mr-1.5" /> IDE</button>
                  </div>
                  {rightTab === "wizard" && (
                    <div className="flex-1 min-h-0 w-full relative">
                      <PlaygroundWizard graph={graph} update={update} onLaunchClick={() => setRightTab("launch")} />
                    </div>
                  )}
                  {rightTab === "playground" && (
                    <div className="flex-1 min-h-0 flex flex-col w-full relative">
                      {isForeignToken ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-black/20 text-white/40">
                          <LineChart size={48} className="mb-4 text-white/20" />
                          <h3 className="text-lg font-bold text-white mb-2">Viewing Foreign Token</h3>
                          <p className="text-sm max-w-sm mb-6">
                            You are currently viewing a live token from the Nursery. The live chart and tape are displayed in the panel to the left.
                          </p>
                          <button 
                            className="btn bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 px-6 py-2 border border-emerald-500/30"
                            onClick={() => setGraph(makeSampleGraph())}
                          >
                            Return to Local Deployment Setup
                          </button>
                        </div>
                      ) : (
                        <Playground graph={graph} ledgers={ledgers} update={update} onLaunchClick={() => setRightTab("launch")} onCreateWallet={createWalletAt} validation={validation} funding={funding} undo={undo} redo={redo} />
                      )}
                    </div>
                  )}
                  {rightTab === "launch" && (
                    <div className="panel launch-wrap flex-1 min-h-0 overflow-y-auto"><LaunchPanel graph={graph} update={update} validation={validation} live={live} /></div>
                  )}
                  {rightTab === "airdrops" && (
                    <div className="flex-1 min-h-0 relative w-full">
                      <AirdropsTab graph={graph} update={update} activeMint={graph.name} />
                    </div>
                  )}
                  {rightTab === "bubblemap" && (
                    <div className="flex-1 flex flex-col h-full min-h-0 w-full">
                      <BubbleMapPanel graph={graph} ledgers={ledgers} sim={sim} />
                    </div>
                  )}
                  {rightTab === "ide" && (
                    <div className="flex-1 flex flex-col h-full bg-[var(--color-bg-editor)]">
                      <AlgoAdvancedIde />
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </div>
      </div>
    </div>
  );
}

function Stat({ v, l, warn, color }: { v: string; l: string; warn?: boolean; color?: string }) {
  return (
    <div className={`astat ${warn ? "warn" : ""}`}>
      <div className="v" style={color ? { color } : undefined}>{v}</div>
      <div className="l">{l}</div>
    </div>
  );
}

type MobileAlgoProps = {
  graph: FlowGraph;
  sim: ReturnType<typeof runGraph>;
  validation: ReturnType<typeof validateGraph>;
  blocks: number;
  funding: { funded: number; needed: number; live: boolean };
  ledgers: Map<string, WalletLedger>;
  update: (fn: (d: FlowGraph) => void) => void;
  backend: ReturnType<typeof useBackend>;
  live: boolean;
  undo: () => void;
  redo: () => void;
  createWalletAt: (x: number, y: number) => void;
  save: () => void;
  load: (f: File) => void;
  setGraph: React.Dispatch<React.SetStateAction<FlowGraph>>;
};

function MobileAlgoWorkspace({ graph, sim, validation, blocks, funding, ledgers, update, live, undo, redo, createWalletAt, save, load }: MobileAlgoProps) {
  const [tab, setTab] = useState<"playground" | "wizard" | "launch" | "chart" | "bubblemap" | "ide">("wizard");
  const fileRef = useRef<HTMLInputElement | null>(null);

  const TABS = [
    { id: "wizard" as const, label: "Wizard", Icon: Layers },
    { id: "playground" as const, label: "Graph", Icon: Layers },
    { id: "launch" as const, label: "Launch", Icon: Cpu },
    { id: "chart" as const, label: "Sim", Icon: LineChart },
    { id: "bubblemap" as const, label: "Bubbles", Icon: CircleDot },
    { id: "ide" as const, label: "IDE", Icon: Code2 },
  ];

  return (
    <div className="algo-launcher-theme flex flex-col h-[100dvh] w-screen overflow-hidden bg-[var(--color-bg-editor)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-safe pt-3 pb-3 border-b border-white/10 bg-[var(--color-bg-editor)] shrink-0 z-10">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
        >
          <ChevronLeft className="size-5" />
          <span className="text-sm font-semibold">Back</span>
        </button>
        <div className="flex flex-col items-center">
          <span className="text-white font-black text-sm">{graph.name || "Algo Deploy"}</span>
          <div className="flex items-center gap-2 mt-0.5">
            {!live ? (
              <span className="text-[9px] font-black uppercase tracking-widest text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">DEMO</span>
            ) : (
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">ALPHA</span>
            )}
            {blocks > 0 && <span className="text-[9px] font-black text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">⛔ {blocks} blocked</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors" onClick={save} title="Save">
            <Download size={16} />
          </button>
          <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors" onClick={() => fileRef.current?.click()} title="Load">
            <Upload size={16} />
          </button>
          <input ref={fileRef} type="file" accept="application/json" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) load(f); e.target.value = ""; }} />
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "wizard" && (
          <div className="h-full flex flex-col pb-28 min-h-0">
            <PlaygroundWizard graph={graph} update={update} onLaunchClick={() => setTab("launch")} />
          </div>
        )}
        {tab === "playground" && (
          <div className="h-full flex flex-col pb-28 min-h-0">
            <Playground
              graph={graph}
              ledgers={ledgers}
              update={update}
              onLaunchClick={() => setTab("launch")}
              onCreateWallet={createWalletAt}
              validation={validation}
              funding={funding}
              undo={undo}
              redo={redo}
            />
          </div>
        )}
        {tab === "launch" && (
          <div className="h-full overflow-y-auto pb-28 custom-scrollbar">
            <div className="panel launch-wrap p-4">
              <LaunchPanel graph={graph} update={update} validation={validation} live={live} />
            </div>
          </div>
        )}
        {tab === "chart" && (
          <div className="h-full flex flex-col pb-28">
            <div className="px-4 py-2 flex items-center gap-3 border-b border-white/5 shrink-0">
              <label className="text-white/40 text-xs font-bold uppercase tracking-wider">Dev buy (SOL)</label>
              <input
                className="field w-16 text-xs"
                type="number" min="0" step="0.1"
                value={graph.devBuySol}
                onChange={(e) => update((d) => { d.devBuySol = Math.max(0, Number(e.target.value) || 0); })}
              />
              <span className="text-white/30 text-xs">SOL ${graph.solUsd.toFixed(2)}</span>
            </div>
            <div className="flex-1 min-h-0">
              <SimChartPanel prints={sim.prints} markers={sim.markers} solUsd={graph.solUsd} title="" />
            </div>
          </div>
        )}
        {tab === "bubblemap" && (
          <div className="h-full flex flex-col pb-28 min-h-0">
            <BubbleMapPanel graph={graph} ledgers={ledgers} sim={sim} />
          </div>
        )}
        {tab === "ide" && (
          <div className="h-full flex flex-col pb-28">
            <AlgoAdvancedIde />
          </div>
        )}
      </div>

      {/* Floating bottom nav */}
      <nav className="mobile-tab-bar" style={{ left: 16, right: 16, bottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}>
        {TABS.map(({ id, label, Icon }) => {
          const isActive = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`mobile-tab-bar__item ${isActive ? "mobile-tab-bar__item--active" : ""}`}
            >
              {isActive && <span className="mobile-tab-bar__dot" aria-hidden />}
              <Icon className={`mobile-tab-bar__icon ${isActive ? "mobile-tab-bar__icon--active" : ""}`} />
              <span className={`mobile-tab-bar__label ${isActive ? "mobile-tab-bar__label--active" : ""}`}>{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
